import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Chip,
  Collapse,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Print as PrintIcon,
  Clear as ClearIcon,
  Inventory2 as InventoryIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import ProductoInfo360Dialog from './info360/ProductoInfo360Dialog';
import ProductoInfo360ListButton from './info360/ProductoInfo360ListButton';
import ImprimirEtiquetasProductoHost from '../../components/inventario/ImprimirEtiquetasProductoHost';
import BarcodeIcon from '../../components/inventario/BarcodeIcon';
import ProveedorInlineSearchField from '../../components/ProveedorInlineSearchField';
import ProveedorSearch from '../../components/ProveedorSearch';
import PDFViewer from '../../components/shared/PDFViewer';
import api from '../../services/api';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../utils/apiError';
import * as XLSX from 'xlsx';
import productoService, {
  type UmEquivalenciaDto,
  parseEquivalenciasUmResponse,
} from '../../services/productoService';
import type { Proveedor } from '../../services/proveedorService';
import tipoMonedaService, { type TipoMoneda } from '../../services/tipoMonedaService';
import { labelTipoArticulo } from '../../config/tipoArticuloOptions';
import { catalogosService } from '../../services/catalogosService';
import {
  ProductoFoto,
  getProductoFallbackImage,
  getProductoImageBySize,
  getProductoPrimaryPhoto,
} from '../../utils/productImage';
import { convertirCantidadEntreUm } from '../../utils/umVentaDocumento';
import { getCodigoTipoMoneda, getTipoMonedaDescripcion } from '../../utils/getTipoMonedaDescripcion';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Divider from '@mui/material/Divider';
import { useGridColumns, type GridColumnDef } from '../../hooks/useGridColumns';
import {
  AccionesColumnHeader,
  PersonalizarColumnasDialog,
} from '../../components/shared/PersonalizarColumnasDialog';
import { descubrirColumnasDeItems, presentarCampoGrilla } from '../../utils/gridFieldValue';

interface Producto {
  codigoArticulo: string;
  descripcion: string;
  imagen?: string | null;
  fotos?: ProductoFoto[] | null;
  nombreLargo?: string;
  codBarra?: string;
  codigoProveedor?: string;
  codigoArticuloProveedor?: string | null;
  codProveedor?: number | null;
  precioVenta?: number;
  cantidad?: number;
  stockMin?: number;
  stockMax?: number;
  costo?: number;
  tipoMoneda?: number | null;
  codigoRamo?: string;
  codigoLinea?: string;
  codigoClase?: string;
  nombreRamo?: string;
  nombreLinea?: string;
  nombreClase?: string;
  codigoRamoNavigation?: {
    nombreRamo?: string;
  } | null;
  codigoLineaNavigation?: {
    nombreLinea?: string;
  } | null;
  codigoClaseNavigation?: {
    nombreClase?: string;
  } | null;
  unidadMedida?: {
    idUnidadMedida: string;
    descripcion: string;
  };
  idUnidadMedida?: string | null;
  umrefStock?: string | null;
  tipoArticulo?: string | null;
  calculaIva?: string | null;
  ivaRI?: number | null;
  ivaRN?: number | null;
  ivaCF?: number | null;
  ivaEX?: number | null;
  ivaMT?: number | null;
  impInterno1?: number | null;
  impInterno2?: number | null;
  tasaHidrica?: number | null;
  porc_Desc?: number | null;
  controlLoteSerie?: string | null;
  requiereVencimiento?: boolean | null;
  diasAlertaVencimiento?: number | null;
  invetariado?: number | null;
  liquidaComision?: number | null;
  noBonificable?: number | null;
  ctaInventario?: number | null;
  ctaVenta?: number | null;
  ctaCompra?: number | null;
  ctaCostoVenta?: number | null;
  ctaDesc?: number | null;
  ctaCtraPartida?: number | null;
  coberturaDias?: number | null;
  demandaMensual6m?: number | null;
  leadTimeDias?: number | null;
}

/** Columnas compartidas entre plantilla, importación y exportación (round-trip). */
const PRODUCTOS_IMPORT_COLUMNS = [
  'codigoArticulo',
  'nombreLargo',
  'codBarra',
  'descripcion',
  'costo',
  'precioVenta',
  'tipoMoneda',
  'codigoRamo',
  'codigoLinea',
  'codigoClase',
  'idUnidadMedida',
  'umrefStock',
  'stockMin',
  'stockMax',
  'tipoArticulo',
  'calculaIva',
  'ivaRI',
  'ivaRN',
  'ivaCF',
  'ivaEX',
  'ivaMT',
  'impInterno1',
  'impInterno2',
  'tasaHidrica',
  'porc_Desc',
  'codigoProveedor',
  'codigoArticuloProveedor',
  'controlLoteSerie',
  'requiereVencimiento',
  'diasAlertaVencimiento',
  'invetariado',
  'liquidaComision',
  'noBonificable',
  'ctaInventario',
  'ctaVenta',
  'ctaCompra',
  'ctaCostoVenta',
  'ctaDesc',
  'ctaCtraPartida',
] as const;

const PRODUCTOS_IMPORT_OBLIGATORIOS = [
  'codigoArticulo',
  'descripcion',
  'codigoRamo',
  'codigoLinea',
  'codigoClase',
] as const;

const PRODUCTOS_IMPORT_NUMERICOS = [
  'costo',
  'precioVenta',
  'tipoMoneda',
  'stockMin',
  'stockMax',
  'ivaRI',
  'ivaRN',
  'ivaCF',
  'ivaEX',
  'ivaMT',
  'impInterno1',
  'impInterno2',
  'tasaHidrica',
  'porc_Desc',
  'diasAlertaVencimiento',
  'invetariado',
  'liquidaComision',
  'noBonificable',
  'ctaInventario',
  'ctaVenta',
  'ctaCompra',
  'ctaCostoVenta',
  'ctaDesc',
  'ctaCtraPartida',
] as const;

const PRODUCTOS_IMPORT_INSTRUCCIONES: string[][] = [
  ['Columna', 'Descripción', 'Valores aceptados'],
  ['codigoArticulo', 'Código único del artículo', 'Texto alfanumérico (obligatorio)'],
  ['nombreLargo', 'Nombre largo', 'Texto (opcional)'],
  ['codBarra', 'Código de barras', 'Único: no puede repetirse en otro producto (opcional)'],
  ['descripcion', 'Descripción', 'Texto (obligatorio)'],
  ['costo', 'Costo', 'Número (opcional)'],
  ['precioVenta', 'Precio de venta', 'Número (opcional)'],
  ['tipoMoneda', 'Moneda del producto', 'Código numérico de TipoMoneda (opcional; 0 = PESOS). Debe existir en el maestro de monedas'],
  ['codigoRamo', 'Ramo', 'Código ramo existente (obligatorio)'],
  ['codigoLinea', 'Línea', 'Código línea existente (obligatorio)'],
  ['codigoClase', 'Clase', 'Código clase existente (obligatorio)'],
  ['idUnidadMedida', 'Unidad de medida base', 'ID de unidad existente (obligatorio). Con stock ≠ 0 no se puede cambiar'],
  ['umrefStock', 'UM referencia de stock', 'ID de unidad existente (opcional; si vacío usa idUnidadMedida). Con stock ≠ 0 no se puede cambiar'],
  ['stockMin', 'Stock mínimo', 'Número (opcional)'],
  ['stockMax', 'Stock máximo', 'Número (opcional)'],
  ['tipoArticulo', 'Tipo de artículo', 'MP, RV, VD, FIN, SER, DIS, AFO, KIT, PT, SE, INA. Con stock ≠ 0 no se puede cambiar'],
  ['calculaIva', 'Calcula IVA', 'Texto (opcional)'],
  ['ivaRI', 'IVA Responsable Inscripto', 'Número (opcional, ej: 21)'],
  ['ivaRN', 'IVA RN', 'Número (opcional)'],
  ['ivaCF', 'IVA CF', 'Número (opcional)'],
  ['ivaEX', 'IVA Exento', 'Número (opcional)'],
  ['ivaMT', 'IVA Monotributo', 'Número (opcional)'],
  ['impInterno1', 'Impuesto interno 1', 'Número (opcional)'],
  ['impInterno2', 'Impuesto interno 2', 'Número (opcional)'],
  ['tasaHidrica', 'Tasa hídrica', 'Número (opcional)'],
  ['porc_Desc', 'Porcentaje descuento', 'Número (opcional)'],
  ['codigoProveedor', 'Proveedor (Nro)', 'Número de proveedor existente (opcional)'],
  ['codigoArticuloProveedor', 'Código artículo proveedor', 'Texto (opcional)'],
  ['controlLoteSerie', 'Control de lote/serie', 'N = sin control, L = lote, S = serie. Con stock ≠ 0 no se puede cambiar'],
  ['requiereVencimiento', 'Requiere vencimiento', '0/1, true/false, S/N (opcional)'],
  ['diasAlertaVencimiento', 'Días alerta vencimiento', 'Número (opcional)'],
  ['invetariado', 'Inventariado', '0/1. Con stock ≠ 0 no se puede cambiar'],
  ['liquidaComision', 'Liquida comisión', '0/1 (opcional)'],
  ['noBonificable', 'No bonificable', '0/1 (opcional)'],
  ['ctaInventario', 'Cuenta inventario', 'Número de cuenta (opcional)'],
  ['ctaVenta', 'Cuenta venta', 'Número de cuenta (opcional)'],
  ['ctaCompra', 'Cuenta compra', 'Número de cuenta (opcional)'],
  ['ctaCostoVenta', 'Cuenta costo de venta', 'Número de cuenta (opcional)'],
  ['ctaDesc', 'Cuenta descuento', 'Número de cuenta (opcional)'],
  ['ctaCtraPartida', 'Cuenta contrapartida', 'Número de cuenta (opcional)'],
];

const parseFlag01 = (raw: unknown): number | null => {
  if (raw === '' || raw === undefined || raw === null) return null;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw ? 1 : 0;
  const s = String(raw).trim().toUpperCase();
  if (s === '1' || s === 'TRUE' || s === 'S' || s === 'SI' || s === 'SÍ' || s === 'Y') return 1;
  if (s === '0' || s === 'FALSE' || s === 'N' || s === 'NO') return 0;
  const n = Number(s);
  if (!Number.isNaN(n)) return n ? 1 : 0;
  return null;
};

const parseBoolFlexible = (raw: unknown): boolean | null => {
  const flag = parseFlag01(raw);
  if (flag === null) return null;
  return flag === 1;
};

const flagToExport = (value: unknown): number | '' => {
  if (value === true || value === 1 || value === '1') return 1;
  if (value === false || value === 0 || value === '0') return 0;
  if (value == null || value === '') return '';
  const parsed = parseFlag01(value);
  return parsed === null ? '' : parsed;
};

const emptyImportCell = (key: string): string | number => {
  if ((PRODUCTOS_IMPORT_NUMERICOS as readonly string[]).includes(key)) return '';
  return '';
};

const productoToImportRow = (p: Producto): Record<string, string | number | boolean | null | undefined> => {
  const umBase = (p.idUnidadMedida || p.unidadMedida?.idUnidadMedida || '').trim();
  return {
    codigoArticulo: p.codigoArticulo ?? '',
    nombreLargo: p.nombreLargo ?? '',
    codBarra: p.codBarra ?? '',
    descripcion: p.descripcion ?? '',
    costo: p.costo ?? '',
    precioVenta: p.precioVenta ?? '',
    tipoMoneda: p.tipoMoneda ?? '',
    codigoRamo: p.codigoRamo ?? '',
    codigoLinea: p.codigoLinea ?? '',
    codigoClase: p.codigoClase ?? '',
    idUnidadMedida: umBase,
    umrefStock: (p.umrefStock || umBase || '').trim(),
    stockMin: p.stockMin ?? '',
    stockMax: p.stockMax ?? '',
    tipoArticulo: p.tipoArticulo ?? '',
    calculaIva: p.calculaIva ?? '',
    ivaRI: p.ivaRI ?? '',
    ivaRN: p.ivaRN ?? '',
    ivaCF: p.ivaCF ?? '',
    ivaEX: p.ivaEX ?? '',
    ivaMT: p.ivaMT ?? '',
    impInterno1: p.impInterno1 ?? '',
    impInterno2: p.impInterno2 ?? '',
    tasaHidrica: p.tasaHidrica ?? '',
    porc_Desc: p.porc_Desc ?? '',
    codigoProveedor: p.codProveedor ?? '',
    codigoArticuloProveedor: p.codigoArticuloProveedor ?? '',
    controlLoteSerie: (p.controlLoteSerie || 'N').toString().trim().toUpperCase() || 'N',
    requiereVencimiento: flagToExport(p.requiereVencimiento),
    diasAlertaVencimiento: p.diasAlertaVencimiento ?? '',
    invetariado: flagToExport(p.invetariado),
    liquidaComision: flagToExport(p.liquidaComision),
    noBonificable: flagToExport(p.noBonificable),
    ctaInventario: p.ctaInventario ?? '',
    ctaVenta: p.ctaVenta ?? '',
    ctaCompra: p.ctaCompra ?? '',
    ctaCostoVenta: p.ctaCostoVenta ?? '',
    ctaDesc: p.ctaDesc ?? '',
    ctaCtraPartida: p.ctaCtraPartida ?? '',
  };
};

const renderEstadoProductoChip = (tipoArticulo?: string | null) => {
  const tipo = (tipoArticulo ?? '').trim().toUpperCase();
  if (tipo === 'INA') {
    return <Chip label={labelTipoArticulo('INA')} color="error" size="small" />;
  }
  if (tipo === 'DIS') {
    return <Chip label={labelTipoArticulo('DIS')} color="warning" size="small" />;
  }
  return null;
};

interface ProductoPagedResult {
  items: Producto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

type ProductoColumnId =
  | 'codigo'
  | 'foto'
  | 'descripcion'
  | 'ramo'
  | 'linea'
  | 'clase'
  | 'estado'
  | 'precio'
  | 'stock'
  | 'stockUmref'
  | 'acciones';

const PRODUCTOS_COLUMNAS: GridColumnDef<ProductoColumnId>[] = [
  { id: 'codigo', label: 'Código', sortable: true },
  { id: 'foto', label: 'Foto', sortable: false },
  { id: 'descripcion', label: 'Descripción', sortable: true },
  { id: 'ramo', label: 'Ramo', sortable: true, defaultVisible: false },
  { id: 'linea', label: 'Línea', sortable: true, defaultVisible: false },
  { id: 'clase', label: 'Clase', sortable: true, defaultVisible: false },
  { id: 'estado', label: 'Estado', sortable: true, defaultVisible: false },
  { id: 'precio', label: 'Precio', align: 'right', sortable: true },
  { id: 'stock', label: 'Stock', align: 'right', sortable: true },
  {
    id: 'stockUmref',
    label: 'Stock UM ref',
    align: 'right',
    sortable: false,
    defaultVisible: false,
  },
  { id: 'acciones', label: 'Acciones', align: 'center', sortable: false, alwaysVisible: true },
];

const PRODUCTOS_EXPORT_PAGE_SIZE = 100;

const TIPOS_SIN_STOCK = new Set(['FIN', 'KIT', 'SER']);

const productoInformaStock = (tipoArticulo?: string | null): boolean =>
  !TIPOS_SIN_STOCK.has((tipoArticulo ?? '').trim().toUpperCase());

const umBaseProductoListado = (producto: Pick<Producto, 'idUnidadMedida' | 'unidadMedida'>): string =>
  String(producto.idUnidadMedida || producto.unidadMedida?.idUnidadMedida || '')
    .trim()
    .toUpperCase();

const umRefStockProductoListado = (producto: Pick<Producto, 'umrefStock'>): string =>
  String(producto.umrefStock || '')
    .trim()
    .toUpperCase();

/** true si hay UM ref distinta de la base (hay que convertir). */
const productoTieneUmrefDistinta = (producto: Producto): boolean => {
  const umBase = umBaseProductoListado(producto);
  const umRef = umRefStockProductoListado(producto);
  return Boolean(umBase && umRef && umBase !== umRef);
};

const formatCantidadStock = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value - Math.round(value)) < 1e-9) return String(Math.round(value));
  return String(Math.round(value * 1000) / 1000);
};

const formatStockProducto = (producto: Pick<Producto, 'tipoArticulo' | 'cantidad'>): string => {
  if (!productoInformaStock(producto.tipoArticulo)) return '-';
  return formatCantidadStock(Number(producto.cantidad ?? 0));
};

const formatPrecioConMoneda = (
  producto: Pick<Producto, 'precioVenta' | 'tipoMoneda'>,
  tiposMoneda: TipoMoneda[],
): string => {
  const monto = Number(producto.precioVenta ?? 0).toFixed(2);
  const codigo = producto.tipoMoneda ?? 0;
  const found = tiposMoneda.find((t) => getCodigoTipoMoneda(t) === Number(codigo));
  const simbolo = found?.simbolo?.trim();
  const label = getTipoMonedaDescripcion(codigo, tiposMoneda);
  if (simbolo) return `${simbolo} ${monto}`;
  if (label) return `$${monto} ${label}`;
  return `$${monto}`;
};

const COLUMN_ORDEN_API: Partial<Record<ProductoColumnId, string>> = {
  codigo: 'CodigoArticulo',
  descripcion: 'Descripcion',
  ramo: 'CodigoRamo',
  linea: 'CodigoLinea',
  clase: 'CodigoClase',
  estado: 'TipoArticulo',
  precio: 'PrecioVenta',
  stock: 'Cantidad',
};

/** Columna Acciones fija a la derecha al hacer scroll horizontal. */
const accionesStickySx = {
  position: 'sticky' as const,
  right: 0,
  zIndex: 2,
  bgcolor: 'background.paper',
  whiteSpace: 'nowrap' as const,
  minWidth: 120,
  boxShadow: '-6px 0 6px -6px rgba(0,0,0,0.25)',
  '.MuiTableRow-root:hover &': {
    bgcolor: '#f9f9f9',
  },
};

const accionesStickyHeadSx = {
  ...accionesStickySx,
  zIndex: 3,
  bgcolor: '#f5f5f5',
  fontWeight: 600,
  '.MuiTableRow-root:hover &': {
    bgcolor: '#f5f5f5',
  },
};

const Productos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [productosData, setProductosData] = useState<ProductoPagedResult>({
    items: [],
    totalCount: 0,
    pageNumber: 0,
    pageSize: 10,
    totalPages: 0,
  });
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openImportar, setOpenImportar] = useState(false);
  const [importPreviewRows, setImportPreviewRows] = useState<any[]>([]);
  const [importFileName, setImportFileName] = useState<string>('');
  const [isProcessingImport, setIsProcessingImport] = useState(false);
  const [importResult, setImportResult] = useState<{
    total: number;
    exitosos: number;
    conErrores: number;
    mensajesError: string[];
  } | null>(null);
  const [importProgress, setImportProgress] = useState<{
    procesados: number;
    total: number;
    porcentaje: number;
  } | null>(null);
  /** Equivalencias por código para convertir stock → UM ref (null = error/sin datos). */
  const [eqsStockUmrefByCodigo, setEqsStockUmrefByCodigo] = useState<
    Record<string, UmEquivalenciaDto[] | null>
  >({});
  const [tiposMoneda, setTiposMoneda] = useState<TipoMoneda[]>([]);
  const [accionesMenu, setAccionesMenu] = useState<{
    el: HTMLElement;
    producto: Producto;
  } | null>(null);
  const [exportExcelLoading, setExportExcelLoading] = useState(false);
  const [info360Articulo, setInfo360Articulo] = useState<string | null>(null);
  const [etiquetasProducto, setEtiquetasProducto] = useState<{
    codigoArticulo: string;
    descripcion: string;
  } | null>(null);
  const [etiquetasOpen, setEtiquetasOpen] = useState(false);
  const [catalogoDescripciones, setCatalogoDescripciones] = useState<{
    ramos: Record<string, string>;
    lineas: Record<string, string>;
    clases: Record<string, string>;
  }>({
    ramos: {},
    lineas: {},
    clases: {},
  });
  const [orderBy, setOrderBy] = useState<ProductoColumnId | ''>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [stockDesde, setStockDesde] = useState('');
  const [stockHasta, setStockHasta] = useState('');
  const [codigoDesde, setCodigoDesde] = useState('');
  const [codigoHasta, setCodigoHasta] = useState('');
  const [codProveedorFiltro, setCodProveedorFiltro] = useState<number | null>(null);
  const [nombreProveedorFiltro, setNombreProveedorFiltro] = useState('');
  const [proveedorSearchOpen, setProveedorSearchOpen] = useState(false);
  const [printPorProveedorLoading, setPrintPorProveedorLoading] = useState(false);
  const [pdfListadoUrl, setPdfListadoUrl] = useState<string | null>(null);
  const {
    columnasVisibles,
    columnasDefinidas,
    columnasAnchorEl,
    setColumnasAnchorEl,
    gridColumns,
    toggleColumnaVisible,
    moverColumna,
    reordenarColumnas,
    agregarColumna,
    restablecerColumnas,
  } = useGridColumns({
    gridKey: 'productos',
    columns: PRODUCTOS_COLUMNAS,
  });
  const columnasAPI = PRODUCTOS_IMPORT_COLUMNS as unknown as string[];
  const camposObligatorios = PRODUCTOS_IMPORT_OBLIGATORIOS as unknown as string[];
  const camposNumericos = PRODUCTOS_IMPORT_NUMERICOS as unknown as string[];

  const renderProductoImagen = (
    producto: Producto,
    size: 'thumbnail' | 'medium' | 'full' = 'thumbnail',
    dimension = 44,
  ) => {
    const tieneFoto = Boolean(getProductoPrimaryPhoto(producto));
    if (!tieneFoto && Array.isArray(producto.fotos)) {
      const inicial = String(producto.codigoArticulo || '?').trim().charAt(0).toUpperCase() || '?';
      return (
        <Box
          sx={{
            width: dimension,
            height: dimension,
            borderRadius: 1.5,
            border: '1px solid #e8e8e8',
            bgcolor: '#fafafa',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-hidden
        >
          <Typography variant="caption" color="text.disabled" fontWeight={600}>
            {inicial}
          </Typography>
        </Box>
      );
    }

    const fallbackSrc = getProductoFallbackImage();
    const initialSrc = getProductoImageBySize(producto, size) || fallbackSrc;

    return (
      <Box
        sx={{
          width: dimension,
          height: dimension,
          borderRadius: 1.5,
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          bgcolor: '#f5f5f5',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          key={`${producto.codigoArticulo}-${initialSrc}`}
          src={initialSrc}
          alt={`Foto de ${producto.codigoArticulo}`}
          loading="lazy"
          onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
            const target = event.currentTarget;
            target.onerror = null;
            target.src = fallbackSrc;
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </Box>
    );
  };

  const normalizarTexto = (valor?: string | null): string => (valor ?? '').trim();

  const getDescripcionRamo = (producto: Producto): string => {
    const codigo = normalizarTexto(producto.codigoRamo);
    return (
      normalizarTexto(producto.codigoRamoNavigation?.nombreRamo) ||
      normalizarTexto(producto.nombreRamo) ||
      (codigo ? normalizarTexto(catalogoDescripciones.ramos[codigo]) : '') ||
      '-'
    );
  };

  const getDescripcionLinea = (producto: Producto): string => {
    const codigo = normalizarTexto(producto.codigoLinea);
    return (
      normalizarTexto(producto.codigoLineaNavigation?.nombreLinea) ||
      normalizarTexto(producto.nombreLinea) ||
      (codigo ? normalizarTexto(catalogoDescripciones.lineas[codigo]) : '') ||
      '-'
    );
  };

  const getDescripcionClase = (producto: Producto): string => {
    const codigo = normalizarTexto(producto.codigoClase);
    return (
      normalizarTexto(producto.codigoClaseNavigation?.nombreClase) ||
      normalizarTexto(producto.nombreClase) ||
      (codigo ? normalizarTexto(catalogoDescripciones.clases[codigo]) : '') ||
      '-'
    );
  };

  const normalizarParaBusqueda = (valor: string): string =>
    valor
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const palabrasBusqueda = useMemo(
    () =>
      normalizarParaBusqueda(searchTerm)
        .split(/\s+/)
        .filter(Boolean),
    [searchTerm],
  );

  const searchTermApi = useMemo(() => palabrasBusqueda.join(' '), [palabrasBusqueda]);

  const handleRequestSort = (columnId: ProductoColumnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const limpiarFiltrosRango = () => {
    setStockDesde('');
    setStockHasta('');
    setCodigoDesde('');
    setCodigoHasta('');
    setCodProveedorFiltro(null);
    setNombreProveedorFiltro('');
    setPage(0);
  };

  const handleProveedorFiltroSelect = (proveedor: { nroProveedor: number; nombre?: string }) => {
    setCodProveedorFiltro(proveedor.nroProveedor);
    setNombreProveedorFiltro((proveedor.nombre ?? '').trim());
    setPage(0);
  };

  const limpiarProveedorFiltro = () => {
    setCodProveedorFiltro(null);
    setNombreProveedorFiltro('');
    setPage(0);
  };

  const filtrosActivosChips = useMemo(() => {
    const chips: { key: string; label: string; onDelete: () => void }[] = [];
    if (stockDesde.trim() !== '') {
      chips.push({
        key: 'stockDesde',
        label: `Stock desde: ${stockDesde.trim()}`,
        onDelete: () => {
          setStockDesde('');
          setPage(0);
        },
      });
    }
    if (stockHasta.trim() !== '') {
      chips.push({
        key: 'stockHasta',
        label: `Stock hasta: ${stockHasta.trim()}`,
        onDelete: () => {
          setStockHasta('');
          setPage(0);
        },
      });
    }
    if (codigoDesde.trim() !== '') {
      chips.push({
        key: 'codigoDesde',
        label: `Código desde: ${codigoDesde.trim()}`,
        onDelete: () => {
          setCodigoDesde('');
          setPage(0);
        },
      });
    }
    if (codigoHasta.trim() !== '') {
      chips.push({
        key: 'codigoHasta',
        label: `Código hasta: ${codigoHasta.trim()}`,
        onDelete: () => {
          setCodigoHasta('');
          setPage(0);
        },
      });
    }
    if (codProveedorFiltro != null) {
      chips.push({
        key: 'proveedor',
        label: `Proveedor: ${nombreProveedorFiltro || `#${codProveedorFiltro}`}`,
        onDelete: () => limpiarProveedorFiltro(),
      });
    }
    return chips;
  }, [
    stockDesde,
    stockHasta,
    codigoDesde,
    codigoHasta,
    codProveedorFiltro,
    nombreProveedorFiltro,
  ]);

  const buildListParams = useCallback(
    (pageNumber: number, pageSize: number) => {
      const stockDesdeNum = stockDesde.trim() === '' ? undefined : Number(stockDesde);
      const stockHastaNum = stockHasta.trim() === '' ? undefined : Number(stockHasta);
      const ordenApi = orderBy ? COLUMN_ORDEN_API[orderBy] : undefined;
      return {
        pageNumber: pageNumber + 1,
        pageSize,
        searchTerm: searchTermApi || undefined,
        orden: ordenApi,
        direccionOrden: ordenApi ? order.toUpperCase() : undefined,
        stockDesde:
          stockDesdeNum != null && !Number.isNaN(stockDesdeNum) ? stockDesdeNum : undefined,
        stockHasta:
          stockHastaNum != null && !Number.isNaN(stockHastaNum) ? stockHastaNum : undefined,
        codigoDesde: codigoDesde.trim() || undefined,
        codigoHasta: codigoHasta.trim() || undefined,
        codProveedor: codProveedorFiltro ?? undefined,
      };
    },
    [
      codigoDesde,
      codigoHasta,
      codProveedorFiltro,
      order,
      orderBy,
      searchTermApi,
      stockDesde,
      stockHasta,
    ],
  );

  const fetchAllProductosConFiltros = useCallback(async (): Promise<Producto[]> => {
    let pageNum = 0;
    let totalPages = 1;
    const allItems: Producto[] = [];
    do {
      const response = await api.get<ProductoPagedResult>('/api/productos', {
        params: buildListParams(pageNum, PRODUCTOS_EXPORT_PAGE_SIZE),
      });
      allItems.push(...(response.data.items ?? []));
      totalPages = response.data.totalPages ?? 1;
      pageNum += 1;
    } while (pageNum < totalPages);
    return allItems;
  }, [buildListParams]);

  const fetchProductos = useCallback(
    async (pageNumber: number, pageSize: number) => {
      setLoadingProductos(true);
      try {
        const response = await api.get<ProductoPagedResult>('/api/productos', {
          params: buildListParams(pageNumber, pageSize),
        });
        const items: Producto[] = response.data.items ?? [];
        setProductosData({
          ...response.data,
          items,
          pageNumber: (response.data.pageNumber ?? 1) - 1,
        });
      } catch (error) {
        console.error('Error al cargar productos:', error);
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron cargar los productos.'), {
          variant: 'error',
        });
        setProductosData({
          items: [],
          totalCount: 0,
          pageNumber: 0,
          pageSize,
          totalPages: 0,
        });
      } finally {
        setLoadingProductos(false);
      }
    },
    [buildListParams, enqueueSnackbar],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      void fetchProductos(page, rowsPerPage);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [fetchProductos, page, rowsPerPage]);

  const productosPagina = productosData.items;
  const stockUmrefVisible = useMemo(
    () => columnasVisibles.some((c) => c.id === 'stockUmref'),
    [columnasVisibles],
  );
  const columnasParaAgregar = useMemo(
    () => descubrirColumnasDeItems(productosPagina, gridColumns.columnOrder),
    [gridColumns.columnOrder, productosPagina],
  );
  const totalCount = productosData.totalCount;

  useEffect(() => {
    if (!stockUmrefVisible) return;

    const pendientes = productosPagina.filter((p) => {
      if (!productoInformaStock(p.tipoArticulo) || !productoTieneUmrefDistinta(p)) return false;
      return !Object.prototype.hasOwnProperty.call(eqsStockUmrefByCodigo, p.codigoArticulo);
    });
    if (pendientes.length === 0) return;

    let cancelled = false;
    const cargar = async () => {
      const resultados = await Promise.all(
        pendientes.map(async (p) => {
          try {
            const response = await api.get(
              `/api/productos/${encodeURIComponent(p.codigoArticulo)}/equivalencias-um`,
            );
            const detalle = parseEquivalenciasUmResponse(response.data);
            return { codigo: p.codigoArticulo, eqs: detalle.equivalencias ?? [] };
          } catch {
            return { codigo: p.codigoArticulo, eqs: null as UmEquivalenciaDto[] | null };
          }
        }),
      );
      if (cancelled) return;
      setEqsStockUmrefByCodigo((prev) => {
        const next = { ...prev };
        for (const r of resultados) {
          next[r.codigo] = r.eqs;
        }
        return next;
      });
    };
    void cargar();
    return () => {
      cancelled = true;
    };
  }, [stockUmrefVisible, productosPagina, eqsStockUmrefByCodigo]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, totalCount, rowsPerPage]);

  const codigosPaginaKey = useMemo(
    () => productosPagina.map((p) => p.codigoArticulo).join('|'),
    [productosPagina],
  );

  useEffect(() => {
    if (!codigosPaginaKey) return;
    let cancelled = false;
    const codigos = codigosPaginaKey.split('|').filter(Boolean);
    void productoService
      .getProductosInfo360Semaforo(codigos)
      .then((semaforos) => {
        if (cancelled) return;
        const byCodigo = new Map(
          semaforos.map((s) => [s.codigoArticulo.trim().toUpperCase(), s]),
        );
        setProductosData((prev) => {
          let changed = false;
          const nextItems = prev.items.map((item) => {
            const s = byCodigo.get(item.codigoArticulo.trim().toUpperCase());
            if (!s) return item;
            if (
              item.coberturaDias === s.coberturaDias &&
              item.demandaMensual6m === s.demandaMensual6m &&
              item.leadTimeDias === s.leadTimeDias
            ) {
              return item;
            }
            changed = true;
            return {
              ...item,
              coberturaDias: s.coberturaDias,
              demandaMensual6m: s.demandaMensual6m,
              leadTimeDias: s.leadTimeDias,
            };
          });
          return changed ? { ...prev, items: nextItems } : prev;
        });
      })
      .catch((semaforoError) => {
        console.error('Error al cargar semáforo Info 360:', semaforoError);
      });
    return () => {
      cancelled = true;
    };
  }, [codigosPaginaKey]);

  const formatStockUmrefProducto = (
    producto: Producto,
  ): { texto: string; hint?: string } => {
    if (!productoInformaStock(producto.tipoArticulo)) {
      return { texto: '-', hint: 'Este tipo de artículo no informa stock' };
    }
    const umBase = umBaseProductoListado(producto);
    const umRef = umRefStockProductoListado(producto);
    const qtyBase = Number(producto.cantidad ?? 0);
    if (!umRef) {
      return { texto: '—', hint: 'Sin UM de referencia de stock configurada' };
    }
    if (!umBase || umRef === umBase) {
      return {
        texto: formatCantidadStock(qtyBase),
        hint: umRef ? `Igual a UM base (${umRef})` : undefined,
      };
    }
    if (!Object.prototype.hasOwnProperty.call(eqsStockUmrefByCodigo, producto.codigoArticulo)) {
      return { texto: '…', hint: 'Convirtiendo a UM ref…' };
    }
    const eqs = eqsStockUmrefByCodigo[producto.codigoArticulo];
    if (!eqs || eqs.length === 0) {
      return { texto: '—', hint: `Sin equivalencia entre ${umBase} y ${umRef}` };
    }
    const conv = convertirCantidadEntreUm(qtyBase, umBase, umRef, eqs, umBase);
    if (conv == null || !Number.isFinite(conv)) {
      return { texto: '—', hint: `Sin equivalencia entre ${umBase} y ${umRef}` };
    }
    return { texto: formatCantidadStock(conv), hint: `Expresado en ${umRef}` };
  };

  const renderCeldaProducto = (columnId: string, producto: Producto) => {
    switch (columnId) {
      case 'codigo':
        return <TableCell sx={{ fontWeight: 500 }}>{producto.codigoArticulo}</TableCell>;
      case 'foto':
        return <TableCell>{renderProductoImagen(producto, 'thumbnail', 40)}</TableCell>;
      case 'descripcion':
        return <TableCell>{producto.descripcion}</TableCell>;
      case 'ramo':
        return <TableCell>{getDescripcionRamo(producto)}</TableCell>;
      case 'linea':
        return <TableCell>{getDescripcionLinea(producto)}</TableCell>;
      case 'clase':
        return <TableCell>{getDescripcionClase(producto)}</TableCell>;
      case 'estado':
        return <TableCell>{renderEstadoProductoChip(producto.tipoArticulo) ?? '—'}</TableCell>;
      case 'precio':
        return (
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            {formatPrecioConMoneda(producto, tiposMoneda)}
          </TableCell>
        );
      case 'stock':
        return <TableCell align="right">{formatStockProducto(producto)}</TableCell>;
      case 'stockUmref': {
        const stockUmref = formatStockUmrefProducto(producto);
        return (
          <TableCell align="right">
            {stockUmref.hint ? (
              <Tooltip title={stockUmref.hint}>
                <Box component="span" sx={{ color: stockUmref.texto === '—' ? 'text.disabled' : 'inherit' }}>
                  {stockUmref.texto}
                </Box>
              </Tooltip>
            ) : (
              stockUmref.texto
            )}
          </TableCell>
        );
      }
      case 'acciones':
        return (
          <TableCell align="center" sx={accionesStickySx}>
            <Stack direction="row" spacing={0} justifyContent="center" alignItems="center">
              <ProductoInfo360ListButton
                producto={producto}
                onClick={() => setInfo360Articulo(producto.codigoArticulo)}
                compact
              />
              <Tooltip title="Editar producto">
                <IconButton
                  aria-label={`Editar producto ${producto.codigoArticulo}`}
                  onClick={() =>
                    navigate(`/inventario/productos/${encodeURIComponent(producto.codigoArticulo)}`)
                  }
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Más acciones">
                <IconButton
                  aria-label={`Más acciones producto ${producto.codigoArticulo}`}
                  size="small"
                  onClick={(e) => setAccionesMenu({ el: e.currentTarget, producto })}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        );
      default: {
        const campo = presentarCampoGrilla(producto, columnId);
        return (
          <TableCell key={columnId} align={campo.align}>
            {campo.texto}
          </TableCell>
        );
      }
    }
  };

  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        const [ramosResp, lineasResp, clasesResp, monedas] = await Promise.all([
          catalogosService.getRamos(1, 1000, ''),
          catalogosService.getLineas(1, 1000, ''),
          catalogosService.getClases(1, 1000, ''),
          tipoMonedaService.getTiposMoneda(),
        ]);

        const ramos = Object.fromEntries(
          (ramosResp.items || [])
            .map((ramo) => [normalizarTexto(ramo.codigoRamo), normalizarTexto(ramo.nombreRamo)])
            .filter(([codigo, nombre]) => Boolean(codigo && nombre)),
        );

        const lineas = Object.fromEntries(
          (lineasResp.items || [])
            .map((linea) => [normalizarTexto(linea.codigoLinea), normalizarTexto(linea.nombreLinea)])
            .filter(([codigo, nombre]) => Boolean(codigo && nombre)),
        );

        const clases = Object.fromEntries(
          (clasesResp.items || [])
            .map((clase) => [normalizarTexto(clase.codigoClase), normalizarTexto(clase.nombreClase)])
            .filter(([codigo, nombre]) => Boolean(codigo && nombre)),
        );

        setCatalogoDescripciones({ ramos, lineas, clases });
        setTiposMoneda(Array.isArray(monedas) ? monedas : []);
      } catch (error) {
        console.error('Error al cargar descripciones de catálogos:', error);
      }
    };

    cargarCatalogos();
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDelete = async (codigo: string) => {
    try {
      const checkRes = await api.get<{ canDelete: boolean; motivos?: string[]; message?: string }>(
        `/api/productos/${encodeURIComponent(codigo)}/puede-eliminar`
      );
      const check = checkRes.data;
      if (!check.canDelete) {
        enqueueSnackbar(check.message || check.motivos?.[0] || 'No se puede eliminar el producto.', { variant: 'error' });
        return;
      }
      if (!window.confirm('¿Está seguro de eliminar este producto?')) return;
      await api.delete(`/api/productos/${codigo}`);
      enqueueSnackbar('Producto eliminado', { variant: 'success' });
      await fetchProductos(page, rowsPerPage);
    } catch (error: unknown) {
      console.error('Error al eliminar producto:', error);
      const axiosError = error as { response?: { data?: { message?: string; motivos?: string[] } } };
      const msg = axiosError?.response?.data?.message
        || axiosError?.response?.data?.motivos?.[0]
        || 'No se puede eliminar el producto.';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleExportExcelTabla = async () => {
    setExportExcelLoading(true);
    try {
      const allItems = await fetchAllProductosConFiltros();

      if (allItems.length === 0) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
        return;
      }

      const rows = allItems.map((p) => productoToImportRow(p));
      const ws = XLSX.utils.json_to_sheet(rows, { header: [...PRODUCTOS_IMPORT_COLUMNS] });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Productos');
      const wsInstrucciones = XLSX.utils.aoa_to_sheet(PRODUCTOS_IMPORT_INSTRUCCIONES);
      XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Productos_${stamp}.xlsx`);
      enqueueSnackbar('Excel exportado en formato de importación (podés editar y reimportar).', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error al exportar productos a Excel:', error);
      enqueueSnackbar('Error al exportar a Excel.', { variant: 'error' });
    } finally {
      setExportExcelLoading(false);
    }
  };

  const handleImprimirPorProveedor = async () => {
    if (codProveedorFiltro == null) {
      enqueueSnackbar('Seleccioná un proveedor para imprimir el listado.', { variant: 'warning' });
      return;
    }
    setPrintPorProveedorLoading(true);
    try {
      const stockDesdeNum = stockDesde.trim() === '' ? undefined : Number(stockDesde);
      const stockHastaNum = stockHasta.trim() === '' ? undefined : Number(stockHasta);
      const ordenApi = orderBy ? COLUMN_ORDEN_API[orderBy] : undefined;
      const blob = await productoService.getListadoPorProveedorPdf({
        codProveedor: codProveedorFiltro,
        searchTerm: searchTermApi || undefined,
        stockDesde:
          stockDesdeNum != null && !Number.isNaN(stockDesdeNum) ? stockDesdeNum : undefined,
        stockHasta:
          stockHastaNum != null && !Number.isNaN(stockHastaNum) ? stockHastaNum : undefined,
        codigoDesde: codigoDesde.trim() || undefined,
        codigoHasta: codigoHasta.trim() || undefined,
        orden: ordenApi,
        direccionOrden: ordenApi ? order.toUpperCase() : undefined,
      });
      setPdfListadoUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (error) {
      console.error('Error al imprimir productos por proveedor:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo generar el listado PDF.'), {
        variant: 'error',
      });
    } finally {
      setPrintPorProveedorLoading(false);
    }
  };

  const handleDescargarPlantilla = () => {
    try {
      const headers = [...PRODUCTOS_IMPORT_COLUMNS];
      const data = [headers, headers.map((key) => emptyImportCell(key))];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Productos');
      const wsInstrucciones = XLSX.utils.aoa_to_sheet(PRODUCTOS_IMPORT_INSTRUCCIONES);
      XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');
      XLSX.writeFile(wb, 'PlantillaProductos.xlsx');
      enqueueSnackbar('Plantilla Excel descargada correctamente', {
        variant: 'success',
        autoHideDuration: 4000,
      });
    } catch {
      enqueueSnackbar('Error al crear la plantilla Excel', {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  const onImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });
      if (!rows.length) {
        setImportPreviewRows([]);
        setImportFileName('');
        setImportResult(null);
        enqueueSnackbar('El archivo está vacío.', { variant: 'warning', autoHideDuration: 4000 });
        return;
      }
      // Normalizar cabeceras conocidas (alias) antes de guardar la vista previa
      const headerAliases: Record<string, string> = {
        codProveedor: 'codigoProveedor',
        CodProveedor: 'codigoProveedor',
      };
      const normalizedRows = rows.map((r) => {
        const copy: any = { ...r };
        Object.entries(headerAliases).forEach(([from, to]) => {
          if (copy[from] !== undefined && copy[to] === undefined) {
            copy[to] = copy[from];
          }
        });
        return copy;
      });
      setImportPreviewRows(normalizedRows);
      setImportFileName(file.name);
      setImportResult(null);
      enqueueSnackbar('Archivo leído correctamente. Revisa y presiona "Importar" para procesar.', { variant: 'info', autoHideDuration: 4000 });
    } catch (error) {
      setImportPreviewRows([]);
      setImportFileName('');
      setImportResult(null);
      enqueueSnackbar('Error leyendo el archivo Excel (¿archivo corrupto/formato incorrecto?)', { variant: 'error', autoHideDuration: 9000 });
    }
  };

  const handleProcesarImportacionProductos = async () => {
    if (!importPreviewRows.length) return;
    try {
      setIsProcessingImport(true);
      setImportProgress(null);

      // Cargar catálogo de unidades de medida para validar idUnidadMedida
      let unidadesValidas = new Set<string>();
      try {
        const unidadesRes = await api.get('/api/catalogos/unidades-medida', {
          params: { pageNumber: 1, pageSize: 1000 },
        });
        const unidades = Array.isArray(unidadesRes.data?.items) ? unidadesRes.data.items : [];
        unidadesValidas = new Set(
          unidades
            .map((u: any) => u?.idUnidadMedida)
            .filter((v: any) => v != null && v !== '')
            .map((v: any) => String(v).trim().toUpperCase())
        );
      } catch (e) {
        // Si falla el catálogo, seguimos pero marcamos error en cada fila que use unidad
        console.error('Error al cargar catálogo de unidades de medida para importación:', e);
      }

      const rows = importPreviewRows;
      const headers = Object.keys(rows[0] ?? {});
      const noEncontrados = columnasAPI.filter(h => !headers.includes(h));
      if (noEncontrados.length > 0) {
        setImportResult({
          total: rows.length,
          exitosos: 0,
          conErrores: rows.length,
          mensajesError: [`Columnas faltantes en el archivo: ${noEncontrados.join(', ')}`],
        });
        return;
      }

      let fallos = 0;
      const mensajesError: string[] = [];
      const validPayloads: { payload: any; rowIndex: number }[] = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const filaExcel = i + 2; // considerando encabezado en fila 1

        // 1. Validar obligatorios
        const faltanCampos: string[] = [];
        for (const campo of camposObligatorios) {
          const valor = row[campo];
          if (valor === undefined || valor === null || String(valor).toString().trim() === '') {
            faltanCampos.push(campo);
          }
        }
        if (faltanCampos.length > 0) {
          mensajesError.push(`Fila ${filaExcel}: Falta completar campos obligatorios: ${faltanCampos.join(', ')}`);
          fallos++;
          continue;
        }

        // 2. Validar unidad de medida (idUnidadMedida)
        const rawUm = row['idUnidadMedida'];
        const umTexto = (rawUm !== undefined && rawUm !== null) ? String(rawUm).trim() : '';
        if (!umTexto) {
          mensajesError.push(`Fila ${filaExcel}: El campo obligatorio "idUnidadMedida" (unidad de medida) está vacío.`);
          fallos++;
          continue;
        }
        const umNormalizado = umTexto.toUpperCase();
        if (unidadesValidas.size > 0 && !unidadesValidas.has(umNormalizado)) {
          mensajesError.push(`Fila ${filaExcel}: La unidad de medida "${umTexto}" no existe en el maestro de unidades.`);
          fallos++;
          continue;
        }

        const rawUmRef = row['umrefStock'];
        const umRefTexto = (rawUmRef !== undefined && rawUmRef !== null && String(rawUmRef).trim() !== '')
          ? String(rawUmRef).trim().toUpperCase()
          : umNormalizado;
        if (unidadesValidas.size > 0 && !unidadesValidas.has(umRefTexto)) {
          mensajesError.push(`Fila ${filaExcel}: La unidad de referencia de stock "${umRefTexto}" no existe en el maestro de unidades.`);
          fallos++;
          continue;
        }

        const rawControl = row['controlLoteSerie'];
        const controlTexto = (rawControl !== undefined && rawControl !== null && String(rawControl).trim() !== '')
          ? String(rawControl).trim().toUpperCase()
          : 'N';
        if (!['N', 'L', 'S'].includes(controlTexto)) {
          mensajesError.push(`Fila ${filaExcel}: controlLoteSerie inválido ("${rawControl}"). Use N, L o S.`);
          fallos++;
          continue;
        }

        const flagKeys = ['invetariado', 'liquidaComision', 'noBonificable'] as const;
        let flagError = false;
        for (const fk of flagKeys) {
          const raw = row[fk];
          if (raw === '' || raw === undefined || raw === null) continue;
          if (parseFlag01(raw) === null) {
            mensajesError.push(`Fila ${filaExcel}: ${fk} inválido ("${raw}"). Use 0/1.`);
            flagError = true;
            break;
          }
        }
        if (flagError) {
          fallos++;
          continue;
        }

        const rawReqVenc = row['requiereVencimiento'];
        if (rawReqVenc !== '' && rawReqVenc !== undefined && rawReqVenc !== null && parseBoolFlexible(rawReqVenc) === null) {
          mensajesError.push(`Fila ${filaExcel}: requiereVencimiento inválido ("${rawReqVenc}"). Use 0/1, true/false o S/N.`);
          fallos++;
          continue;
        }

        // 3. Validación de tipo numérico (excluye flags y booleanos parseados aparte)
        const camposFlagOBool = new Set<string>([
          ...flagKeys,
          'requiereVencimiento',
        ]);
        const camposTipoMal: string[] = [];
        for (const nCampo of camposNumericos) {
          if (camposFlagOBool.has(nCampo)) continue;
          const raw = row[nCampo];
          if (raw !== '' && raw !== undefined && raw !== null && isNaN(Number(raw))) {
            camposTipoMal.push(nCampo);
          }
        }
        if (camposTipoMal.length > 0) {
          mensajesError.push(`Fila ${filaExcel}: campos inválidos: ${camposTipoMal.join(', ')} (deben ser numéricos)`);
          fallos++;
          continue;
        }

        // 4. Mapear datos para API (CreateProductoDto / upsert)
        const producto: any = {};
        columnasAPI.forEach((key) => {
          const raw = row[key];
          if (key === 'idUnidadMedida') {
            producto[key] = umNormalizado;
          } else if (key === 'umrefStock') {
            producto[key] = umRefTexto;
          } else if (key === 'controlLoteSerie') {
            producto[key] = controlTexto;
          } else if (key === 'requiereVencimiento') {
            producto[key] = parseBoolFlexible(raw);
          } else if (flagKeys.includes(key as typeof flagKeys[number])) {
            producto[key] = parseFlag01(raw);
          } else if (key === 'codigoProveedor') {
            // Se mapea a codProveedor más abajo
          } else if (camposNumericos.includes(key)) {
            producto[key] = (raw !== '' && raw !== undefined && raw !== null) ? Number(raw) : null;
          } else if (
            key.startsWith('codigo') ||
            key.startsWith('id') ||
            key === 'tipoArticulo' ||
            typeof raw === 'string'
          ) {
            producto[key] = (raw !== '' && raw !== undefined && raw !== null) ? String(raw) : '';
          } else {
            producto[key] = (raw !== '' && raw !== undefined) ? raw : null;
          }
        });

        const rawProv = row['codigoProveedor'] ?? row['codProveedor'];
        if (rawProv !== '' && rawProv !== undefined && rawProv !== null) {
          const nProv = Number(rawProv);
          if (!Number.isNaN(nProv)) {
            producto.codProveedor = nProv;
          }
        }
        delete producto.codigoProveedor;

        // Si viene Costo en el Excel y no se especifica CostoCatalogo,
        // usarlo también como costoCatalogo para que funcione la fórmula de precios.
        if (producto.costo != null && producto.costo !== '' &&
            (producto.costoCatalogo === undefined || producto.costoCatalogo === null || producto.costoCatalogo === '')) {
          producto.costoCatalogo = Number(producto.costo);
        }

        // Lógica de precio neto y porcentaje de ganancia:
        // - Si precioVenta viene vacío: usar costo como precioVenta y porcentajeGanan = 0
        // - Si precioVenta viene con valor y costo > 0: calcular porcentajeGanan
        const costoVal = producto.costo != null && producto.costo !== '' ? Number(producto.costo) : null;
        const tienePrecio = producto.precioVenta != null && producto.precioVenta !== '' && !isNaN(Number(producto.precioVenta));

        if (!tienePrecio && costoVal != null && !isNaN(costoVal)) {
          producto.precioVenta = costoVal;
          producto.porcentajeGanan = 0;
        } else if (tienePrecio && costoVal != null && !isNaN(costoVal) && costoVal > 0) {
          const pn = Number(producto.precioVenta);
          const pct = ((pn / costoVal) - 1) * 100;
          producto.porcentajeGanan = Math.round(pct * 100) / 100;
        }

        validPayloads.push({ payload: producto, rowIndex: filaExcel });
      }

      const barcodeFilas = new Map<string, number>();
      const sinDuplicadosArchivo: { payload: any; rowIndex: number }[] = [];
      for (const item of validPayloads) {
        const cb = String(item.payload.codBarra || '').trim();
        if (!cb) {
          sinDuplicadosArchivo.push(item);
          continue;
        }
        const key = cb.toUpperCase();
        const filaPrevia = barcodeFilas.get(key);
        if (filaPrevia != null) {
          mensajesError.push(
            `Fila ${item.rowIndex}: el código de barras "${cb}" ya está en la fila ${filaPrevia}. No puede repetirse.`,
          );
          fallos++;
          continue;
        }
        barcodeFilas.set(key, item.rowIndex);
        sinDuplicadosArchivo.push(item);
      }

      const payloadsUnicos: { payload: any; rowIndex: number }[] = [];
      for (const item of sinDuplicadosArchivo) {
        const cb = String(item.payload.codBarra || '').trim();
        if (!cb) {
          payloadsUnicos.push(item);
          continue;
        }
        try {
          const otro = await productoService.codigoBarraEnUsoPorOtro(cb, item.payload.codigoArticulo);
          if (otro) {
            mensajesError.push(
              `Fila ${item.rowIndex}: el código de barras "${cb}" ya está en el producto ${otro.codigoArticulo}.`,
            );
            fallos++;
            continue;
          }
        } catch {
          /* si no se puede consultar, el alta puede fallar en el servidor */
        }
        payloadsUnicos.push(item);
      }

      if (payloadsUnicos.length === 0) {
        setImportResult({
          total: rows.length,
          exitosos: 0,
          conErrores: fallos,
          mensajesError,
        });
        return;
      }

      const totalImport = payloadsUnicos.length;
      const pctAprox = (procesados: number) => {
        if (totalImport <= 0) return 0;
        const raw = (procesados / totalImport) * 100;
        return Math.min(100, Math.floor(raw / 5) * 5);
      };
      setImportProgress({ procesados: 0, total: totalImport, porcentaje: 0 });
      let exitos = 0;
      const batchSize = 100;
      let ultimoPorcentajeMostrado = 0;

      for (let start = 0; start < payloadsUnicos.length; start += batchSize) {
        const chunk = payloadsUnicos.slice(start, start + batchSize);
        const payloads = chunk.map((x) => x.payload);
        try {
          const res = await productoService.createProductosBulk(payloads);
          exitos += res.exitosos;
          fallos += res.fallidos;
          res.errores.forEach((e) => {
            const rowIndex = chunk[e.indice]?.rowIndex ?? (start + e.indice + 2);
            mensajesError.push(`Fila ${rowIndex} (artículo ${e.codigoArticulo}): ${e.mensaje}`);
          });
        } catch (err: any) {
          const data = err?.response?.data;
          const msgBase = data?.message || err?.message || 'Error al procesar el lote';
          mensajesError.push(
            `Lote filas ${chunk[0]?.rowIndex ?? start + 2}-${chunk[chunk.length - 1]?.rowIndex ?? start + batchSize + 1}: ${String(msgBase)}`
          );
          fallos += chunk.length;
        }
        const procesados = Math.min(start + batchSize, totalImport);
        const porcentaje = pctAprox(procesados);
        if (porcentaje !== ultimoPorcentajeMostrado || procesados === totalImport) {
          ultimoPorcentajeMostrado = porcentaje;
          setImportProgress({ procesados, total: totalImport, porcentaje });
        }
      }

      setImportResult({
        total: rows.length,
        exitosos: exitos,
        conErrores: fallos,
        mensajesError,
      });
      fetchProductos(page, rowsPerPage);
    } finally {
      setIsProcessingImport(false);
      setImportProgress(null);
    }
  };

  return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con controles */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Productos
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button
                variant="text"
                startIcon={<InventoryIcon />}
                onClick={() => navigate('/inventario/lotes')}
                sx={{ textTransform: 'none', fontWeight: 500 }}
              >
                Lotes
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                onClick={() => setOpenImportar(true)}
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 500 }}
              >
                {isMobile ? 'Importar' : 'Importar'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={() => void handleExportExcelTabla()}
                disabled={exportExcelLoading}
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 500 }}
              >
                {exportExcelLoading ? 'Exportando...' : 'Exportar'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/inventario/productos/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                {isMobile ? 'Nuevo' : 'Nuevo producto'}
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField
                label="Buscar"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" spacing={1} flexShrink={0}>
                <Button
                  variant={filtrosActivosChips.length > 0 || filtrosAbiertos ? 'contained' : 'outlined'}
                  color={filtrosActivosChips.length > 0 ? 'primary' : 'inherit'}
                  size="small"
                  startIcon={<FilterListIcon />}
                  onClick={() => setFiltrosAbiertos((v) => !v)}
                  sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
                >
                  Filtros{filtrosActivosChips.length > 0 ? ` (${filtrosActivosChips.length})` : ''}
                </Button>
              </Stack>
            </Stack>
            {filtrosActivosChips.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                {filtrosActivosChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    size="small"
                    label={chip.label}
                    onDelete={chip.onDelete}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
                <Chip
                  size="small"
                  label="Limpiar filtros"
                  variant="outlined"
                  onClick={limpiarFiltrosRango}
                  onDelete={limpiarFiltrosRango}
                  deleteIcon={<ClearIcon />}
                  sx={{ borderRadius: 2 }}
                />
              </Stack>
            )}
            <Collapse in={filtrosAbiertos}>
              <Box
                sx={{
                  mt: 2,
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: '#fafafa',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={6} sm={6} md={4} lg={1.5}>
                    <TextField
                      fullWidth
                      label="Stock desde"
                      size="small"
                      type="number"
                      value={stockDesde}
                      onChange={(e) => {
                        setStockDesde(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={4} lg={1.5}>
                    <TextField
                      fullWidth
                      label="Stock hasta"
                      size="small"
                      type="number"
                      value={stockHasta}
                      onChange={(e) => {
                        setStockHasta(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={4} lg={1.5}>
                    <TextField
                      fullWidth
                      label="Código desde"
                      size="small"
                      value={codigoDesde}
                      onChange={(e) => {
                        setCodigoDesde(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={4} lg={1.5}>
                    <TextField
                      fullWidth
                      label="Código hasta"
                      size="small"
                      value={codigoHasta}
                      onChange={(e) => {
                        setCodigoHasta(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8} md={6} lg={4}>
                    <ProveedorInlineSearchField
                      label="Proveedor"
                      placeholder={isMobile ? 'Nro, nombre o CUIT' : 'Número, nombre o CUIT'}
                      helperText=""
                      size="small"
                      displayValue={
                        codProveedorFiltro != null
                          ? `${nombreProveedorFiltro || 'Proveedor'} - ${codProveedorFiltro}`
                          : ''
                      }
                      onSelect={handleProveedorFiltroSelect}
                      onOpenModal={() => setProveedorSearchOpen(true)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={2} lg={2}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: '100%' }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {codProveedorFiltro != null && (
                          <IconButton
                            size="small"
                            onClick={limpiarProveedorFiltro}
                            title="Quitar proveedor"
                            aria-label="Quitar proveedor"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Tooltip title="Imprimir por proveedor">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => void handleImprimirPorProveedor()}
                              disabled={
                                codProveedorFiltro == null ||
                                printPorProveedorLoading ||
                                loadingProductos
                              }
                              aria-label="Imprimir por proveedor"
                            >
                              {printPorProveedorLoading ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      <Button
                        variant="text"
                        size="small"
                        onClick={limpiarFiltrosRango}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap', ml: 'auto', pr: 0 }}
                      >
                        {isMobile ? 'Limpiar' : 'Limpiar filtros'}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {productosPagina.map((producto) => {
                const estadoChip = renderEstadoProductoChip(producto.tipoArticulo);
                return (
                <Card 
                  key={producto.codigoArticulo}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {renderProductoImagen(producto, 'thumbnail', 56)}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                            {producto.descripcion}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Código:</strong> {producto.codigoArticulo}
                          </Typography>
                          {estadoChip && (
                            <Box sx={{ mt: 0.5 }}>{estadoChip}</Box>
                          )}
                        </Box>
                      </Stack>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Ramo:</strong> {getDescripcionRamo(producto)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Línea:</strong> {getDescripcionLinea(producto)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Clase:</strong> {getDescripcionClase(producto)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Precio:</strong> {formatPrecioConMoneda(producto, tiposMoneda)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: !productoInformaStock(producto.tipoArticulo)
                              ? 'text.secondary'
                              : (producto.cantidad || 0) > 0
                                ? 'primary.main'
                                : 'error.main',
                          }}
                        >
                          <strong>Stock:</strong> {formatStockProducto(producto)}
                        </Typography>
                        {stockUmrefVisible && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Stock UM ref:</strong> {formatStockUmrefProducto(producto).texto}
                            {umRefStockProductoListado(producto)
                              ? ` ${umRefStockProductoListado(producto)}`
                              : ''}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} alignItems="center">
                        <ProductoInfo360ListButton
                          producto={producto}
                          onClick={() => setInfo360Articulo(producto.codigoArticulo)}
                        />
                        <Tooltip title="Editar producto">
                          <IconButton
                            aria-label={`Editar producto ${producto.codigoArticulo}`}
                            onClick={() =>
                              navigate(`/inventario/productos/${encodeURIComponent(producto.codigoArticulo)}`)
                            }
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Más acciones">
                          <IconButton
                            aria-label={`Más acciones producto ${producto.codigoArticulo}`}
                            size="small"
                            onClick={(e) => setAccionesMenu({ el: e.currentTarget, producto })}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
              })}
              {loadingProductos && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              {!loadingProductos && productosPagina.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay productos para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                overflowX: 'auto',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    {columnasVisibles.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.align}
                        sx={col.id === 'acciones' ? accionesStickyHeadSx : { fontWeight: 600 }}
                        sortDirection={orderBy === col.id ? order : false}
                      >
                        {col.id === 'acciones' ? (
                          <AccionesColumnHeader
                            label={col.label}
                            onCustomize={(event) => setColumnasAnchorEl(event.currentTarget)}
                          />
                        ) : col.sortable ? (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : 'asc'}
                            onClick={() => handleRequestSort(col.id as ProductoColumnId)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingProductos ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(columnasVisibles.length, 1)} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : productosPagina.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(columnasVisibles.length, 1)} align="center" sx={{ py: 4 }}>
                        No hay productos para mostrar
                      </TableCell>
                    </TableRow>
                  ) : (
                    productosPagina.map((producto) => (
                      <TableRow
                        key={producto.codigoArticulo}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        {columnasVisibles.map((col) => (
                          <React.Fragment key={col.id}>
                            {renderCeldaProducto(col.id, producto)}
                          </React.Fragment>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <PersonalizarColumnasDialog
        anchorEl={columnasAnchorEl}
        onClose={() => setColumnasAnchorEl(null)}
        columns={columnasDefinidas}
        columnOrder={gridColumns.columnOrder}
        visibleIds={gridColumns.visibleIds}
        onToggle={toggleColumnaVisible}
        onMove={moverColumna}
        onReorder={reordenarColumnas}
        onReset={restablecerColumnas}
        addableColumns={columnasParaAgregar}
        onAdd={agregarColumna}
      />

      <Menu
        anchorEl={accionesMenu?.el ?? null}
        open={Boolean(accionesMenu)}
        onClose={() => setAccionesMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const producto = accionesMenu?.producto;
            setAccionesMenu(null);
            if (!producto) return;
            setEtiquetasProducto({
              codigoArticulo: producto.codigoArticulo,
              descripcion: producto.descripcion || '',
            });
            setEtiquetasOpen(true);
          }}
        >
          <ListItemIcon>
            <BarcodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Imprimir etiquetas</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const codigo = accionesMenu?.producto.codigoArticulo;
            setAccionesMenu(null);
            if (codigo) {
              navigate(`/inventario/productos/nuevo?copiarDe=${encodeURIComponent(codigo)}`);
            }
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copiar</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const codigo = accionesMenu?.producto.codigoArticulo;
            setAccionesMenu(null);
            if (codigo) void handleDelete(codigo);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Eliminar</ListItemText>
        </MenuItem>
      </Menu>

      {/* Modal de importación */}
      <Dialog open={openImportar} onClose={() => setOpenImportar(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap:1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Productos</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            {/* Bloque 1: Descargar plantilla */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Descarga la plantilla Excel (mismo formato que Exportar). También podés exportar el listado, editarlo y reimportarlo.
              </Typography>
              <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* Bloque 2: Importar desde archivo */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudUploadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Seleccioná el Excel: si el código ya existe se actualiza; si no, se crea. Con stock distinto de cero no se pueden cambiar UM, tipo de artículo ni control de lote.
              </Typography>
              <label htmlFor="excel-upload-productos">
                <input
                  id="excel-upload-productos"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={onImportChange}
                />
                <Button variant="contained" fullWidth color="primary" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                  Seleccionar Archivo
                </Button>
              </label>
              {importFileName && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="body2" fontWeight={600}>
                      {importFileName}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!importPreviewRows.length || isProcessingImport}
                    onClick={handleProcesarImportacionProductos}
                    startIcon={isProcessingImport ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                    sx={{ fontWeight: 700 }}
                  >
                    {isProcessingImport
                      ? (importProgress
                        ? `Procesando ${importProgress.porcentaje}%`
                        : 'Procesando...')
                      : 'Importar'}
                  </Button>
                </Box>
              )}
              {importResult && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Resultados de la Importación
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: importResult.conErrores > 0 ? '#ffebee' : '#ffedd5',
                      border: '1px solid',
                      borderColor: importResult.conErrores > 0 ? '#ffcdd2' : '#fed7aa',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Total procesados:</strong> {importResult.total}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Exitosos (creados o actualizados):</strong> {importResult.exitosos}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Con errores:</strong> {importResult.conErrores}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tasa de éxito:</strong>{' '}
                      {importResult.total > 0
                        ? `${Math.round((importResult.exitosos / importResult.total) * 100)}%`
                        : '0%'}
                    </Typography>
                  </Box>
                  {importResult.mensajesError.length > 0 && (
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #ffe0b2',
                        bgcolor: '#fff3e0',
                        p: 2,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Detalles de errores:
                      </Typography>
                      {importResult.mensajesError.map((m, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{ mb: 0.5, whiteSpace: 'pre-line' }}
                        >
                          {m}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {importResult ? (
            <Button onClick={() => setOpenImportar(false)} variant="contained" color="primary">
              Finalizar
            </Button>
          ) : (
            <Button onClick={() => setOpenImportar(false)} color="inherit">
              Cancelar
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <ProductoInfo360Dialog
        open={info360Articulo != null}
        codigoArticulo={info360Articulo}
        onClose={() => setInfo360Articulo(null)}
      />
      {etiquetasProducto ? (
        <ImprimirEtiquetasProductoHost
          open={etiquetasOpen}
          codigoArticulo={etiquetasProducto.codigoArticulo}
          descripcion={etiquetasProducto.descripcion}
          onClose={() => setEtiquetasOpen(false)}
        />
      ) : null}
      <ProveedorSearch
        open={proveedorSearchOpen}
        onClose={() => setProveedorSearchOpen(false)}
        onSelect={(proveedor: Proveedor) => {
          handleProveedorFiltroSelect({
            nroProveedor: proveedor.nroProveedor,
            nombre: proveedor.nombre,
          });
          setProveedorSearchOpen(false);
        }}
      />
      {pdfListadoUrl ? (
        <PDFViewer
          pdfUrl={pdfListadoUrl}
          title={
            nombreProveedorFiltro
              ? `Productos — ${nombreProveedorFiltro}`
              : `Productos — proveedor #${codProveedorFiltro}`
          }
          documentId={`productos-proveedor-${codProveedorFiltro ?? 0}`}
          documentType="informeCliente"
          downloadFileName={`Productos_proveedor_${codProveedorFiltro ?? 'sin'}_${new Date()
            .toISOString()
            .slice(0, 10)}.pdf`}
          onClose={() => {
            setPdfListadoUrl((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return null;
            });
          }}
        />
      ) : null}
      </Box>
  );
};

export default Productos;